import { 
  DevicePhoneMobileIcon, 
  ComputerDesktopIcon, 
  DeviceTabletIcon,
  GlobeAltIcon,
  ClockIcon,
  SignalIcon,
  CpuChipIcon,
  LinkIcon,
  CursorArrowRaysIcon,
  FingerPrintIcon
} from '@heroicons/react/24/outline';
import { Card } from '@realvisor/ui';

interface VisitorMetadataProps {
  metadata: {
    // Device & Screen
    screenWidth?: number;
    screenHeight?: number;
    viewportWidth?: number;
    viewportHeight?: number;
    deviceType?: string;
    isTouchDevice?: boolean;
    platform?: string;
    colorDepth?: number;
    pixelRatio?: number;
    
    // Browser & Language
    userAgent?: string;
    language?: string;
    languages?: string[];
    timezone?: string;
    timezoneOffset?: number;
    cookiesEnabled?: boolean;
    doNotTrack?: boolean;
    online?: boolean;
    
    // Connection & Hardware
    connectionType?: string;
    connectionDownlink?: number;
    deviceMemory?: number;
    hardwareConcurrency?: number;
    
    // UTM Tracking
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmTerm?: string;
    utmContent?: string;
    
    // Page & Engagement
    pageUrl?: string;
    pageTitle?: string;
    referrerUrl?: string;
    timeOnPage?: number;
    formInteractions?: number;
    sessionId?: string;
    fingerprint?: string;
  } | null;
}

const DEVICE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  mobile: DevicePhoneMobileIcon,
  tablet: DeviceTabletIcon,
  desktop: ComputerDesktopIcon,
};

const DEVICE_LABELS: Record<string, string> = {
  mobile: 'Mobil',
  tablet: 'Tablet',
  desktop: 'Desktop',
};

function formatTimeOnPage(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

function parseUserAgent(ua: string): { browser: string; os: string } {
  let browser = 'Neznámý';
  let os = 'Neznámý';
  
  // Browser detection
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edg')) browser = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browser = 'Opera';
  
  // OS detection
  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('Linux')) os = 'Linux';
  
  return { browser, os };
}

export function VisitorMetadata({ metadata }: VisitorMetadataProps) {
  if (!metadata) return null;
  
  const hasDeviceInfo = metadata.deviceType || metadata.screenWidth || metadata.platform;
  const hasUtmInfo = metadata.utmSource || metadata.utmMedium || metadata.utmCampaign;
  const hasEngagement = metadata.timeOnPage || metadata.formInteractions;
  const hasConnection = metadata.connectionType || metadata.connectionDownlink;
  
  if (!hasDeviceInfo && !hasUtmInfo && !hasEngagement) return null;
  
  const DeviceIcon = DEVICE_ICONS[metadata.deviceType || 'desktop'] || ComputerDesktopIcon;
  const deviceLabel = DEVICE_LABELS[metadata.deviceType || 'desktop'] || metadata.deviceType;
  
  const { browser, os } = metadata.userAgent ? parseUserAgent(metadata.userAgent) : { browser: '-', os: '-' };
  
  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <FingerPrintIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Informace o návštěvníkovi</h2>
        </div>
        
        <div className="space-y-6">
          {/* Device Info */}
          {hasDeviceInfo && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <DeviceIcon className="h-4 w-4" />
                Zařízení
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {metadata.deviceType && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">Typ:</span>
                    <span className="text-sm font-medium">{deviceLabel}</span>
                    {metadata.isTouchDevice && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Touch</span>
                    )}
                  </div>
                )}
                {metadata.screenWidth && metadata.screenHeight && (
                  <div>
                    <span className="text-sm text-gray-500">Obrazovka: </span>
                    <span className="text-sm font-medium">{metadata.screenWidth}×{metadata.screenHeight}</span>
                    {metadata.pixelRatio && metadata.pixelRatio > 1 && (
                      <span className="text-xs text-gray-400 ml-1">@{metadata.pixelRatio}x</span>
                    )}
                  </div>
                )}
                {metadata.viewportWidth && metadata.viewportHeight && (
                  <div>
                    <span className="text-sm text-gray-500">Viewport: </span>
                    <span className="text-sm font-medium">{metadata.viewportWidth}×{metadata.viewportHeight}</span>
                  </div>
                )}
                {browser !== '-' && (
                  <div>
                    <span className="text-sm text-gray-500">Prohlížeč: </span>
                    <span className="text-sm font-medium">{browser}</span>
                  </div>
                )}
                {os !== '-' && (
                  <div>
                    <span className="text-sm text-gray-500">OS: </span>
                    <span className="text-sm font-medium">{os}</span>
                  </div>
                )}
                {metadata.language && (
                  <div>
                    <span className="text-sm text-gray-500">Jazyk: </span>
                    <span className="text-sm font-medium">{metadata.language}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Connection & Hardware */}
          {(hasConnection || metadata.deviceMemory || metadata.hardwareConcurrency) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <SignalIcon className="h-4 w-4" />
                Připojení & Hardware
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {metadata.connectionType && (
                  <div>
                    <span className="text-sm text-gray-500">Připojení: </span>
                    <span className="text-sm font-medium uppercase">{metadata.connectionType}</span>
                  </div>
                )}
                {metadata.connectionDownlink && (
                  <div>
                    <span className="text-sm text-gray-500">Rychlost: </span>
                    <span className="text-sm font-medium">{metadata.connectionDownlink} Mbps</span>
                  </div>
                )}
                {metadata.deviceMemory && (
                  <div>
                    <span className="text-sm text-gray-500">RAM: </span>
                    <span className="text-sm font-medium">{metadata.deviceMemory} GB</span>
                  </div>
                )}
                {metadata.hardwareConcurrency && (
                  <div>
                    <span className="text-sm text-gray-500">CPU: </span>
                    <span className="text-sm font-medium">{metadata.hardwareConcurrency} jader</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Location & Time */}
          {metadata.timezone && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <ClockIcon className="h-4 w-4" />
                Lokalita
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <span className="text-sm text-gray-500">Časová zóna: </span>
                  <span className="text-sm font-medium">{metadata.timezone}</span>
                </div>
              </div>
            </div>
          )}
          
          {/* UTM Tracking */}
          {hasUtmInfo && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <LinkIcon className="h-4 w-4" />
                Marketing (UTM)
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {metadata.utmSource && (
                  <div>
                    <span className="text-sm text-gray-500">Zdroj: </span>
                    <span className="text-sm font-medium">{metadata.utmSource}</span>
                  </div>
                )}
                {metadata.utmMedium && (
                  <div>
                    <span className="text-sm text-gray-500">Médium: </span>
                    <span className="text-sm font-medium">{metadata.utmMedium}</span>
                  </div>
                )}
                {metadata.utmCampaign && (
                  <div>
                    <span className="text-sm text-gray-500">Kampaň: </span>
                    <span className="text-sm font-medium">{metadata.utmCampaign}</span>
                  </div>
                )}
                {metadata.utmTerm && (
                  <div>
                    <span className="text-sm text-gray-500">Klíčové slovo: </span>
                    <span className="text-sm font-medium">{metadata.utmTerm}</span>
                  </div>
                )}
                {metadata.utmContent && (
                  <div>
                    <span className="text-sm text-gray-500">Obsah: </span>
                    <span className="text-sm font-medium">{metadata.utmContent}</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Engagement */}
          {hasEngagement && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <CursorArrowRaysIcon className="h-4 w-4" />
                Engagement
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {metadata.timeOnPage !== undefined && (
                  <div>
                    <span className="text-sm text-gray-500">Čas na stránce: </span>
                    <span className="text-sm font-medium">{formatTimeOnPage(metadata.timeOnPage)}</span>
                  </div>
                )}
                {metadata.formInteractions !== undefined && (
                  <div>
                    <span className="text-sm text-gray-500">Interakce: </span>
                    <span className="text-sm font-medium">{metadata.formInteractions}×</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Page Info */}
          {(metadata.pageUrl || metadata.referrerUrl) && (
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <GlobeAltIcon className="h-4 w-4" />
                Stránka
              </h3>
              <div className="space-y-2">
                {metadata.pageUrl && (
                  <div>
                    <span className="text-sm text-gray-500">URL: </span>
                    <a 
                      href={metadata.pageUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:underline break-all"
                    >
                      {metadata.pageUrl}
                    </a>
                  </div>
                )}
                {metadata.referrerUrl && (
                  <div>
                    <span className="text-sm text-gray-500">Referrer: </span>
                    <a 
                      href={metadata.referrerUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:underline break-all"
                    >
                      {metadata.referrerUrl}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Technical IDs */}
          {(metadata.sessionId || metadata.fingerprint) && (
            <div className="pt-3 border-t border-gray-100">
              <div className="flex flex-wrap gap-4 text-xs text-gray-400">
                {metadata.sessionId && (
                  <span>Session: {metadata.sessionId}</span>
                )}
                {metadata.fingerprint && (
                  <span>Fingerprint: {metadata.fingerprint}</span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
